import { SelectElement, TextFieldElement, UseFormReturn } from '@graphcommerce/ecommerce-ui'
import {
  ConfigurableProduct,
  CustomizableDropDownValue,
  CustomizableFieldOption,
  CustomizableOptionInterface,
  CustomizableProductInterface,
  FourOverCustomOptionModifier,
} from '@graphcommerce/graphql-mesh'
import { useFormAddProductsToCart } from '@graphcommerce/magento-product/components/AddProductsToCart'
import { ProductCustomizableFragment } from '@graphcommerce/magento-product/components/ProductCustomizable//ProductCustomizable.gql'
import { Money } from '@graphcommerce/magento-store'
import { filterNonNullableKeys, RenderType, TypeRenderer } from '@graphcommerce/next-ui'
import { MenuItem } from '@mui/material'
import React, { useCallback, useEffect } from 'react'
import { Dropdown } from '../../Dropdown'
import { StandardDropdown } from '../../Dropdown/StandardDropdown'
import { ColorspecDropDownOption } from './Colorspec'
import { TurnAroundTimeDropDownOption } from './TurnAroundTime'

export type OptionTypeRenderer = TypeRenderer<
  NonNullable<NonNullable<ProductCustomizableFragment['options']>[number]> & {
    optionIndex: number
    index: number
  }
>

const CustomizableAreaOption = React.memo<
  React.ComponentProps<OptionTypeRenderer['CustomizableAreaOption']>
>((props) => {
  const { uid, areaValue, required, optionIndex, index, title } = props
  const maxLength = areaValue?.max_characters ?? undefined
  const { control, register } = useFormAddProductsToCart()

  return (
    <>
      <input
        type='hidden'
        {...register(`cartItems.${index}.entered_options.${optionIndex}.uid`)}
        value={uid}
      />
      <TextFieldElement
        color='primary'
        multiline
        minRows={3}
        control={control}
        name={`cartItems.${index}.entered_options.${optionIndex}.value`}
        label={title}
        required={Boolean(required)}
        validation={{ maxLength }}
        helperText={(maxLength ?? 0) > 0 && `A maximum of ${maxLength}`}
      />
    </>
  )
})

const CustomizableDropDownOption = React.memo<
  React.ComponentProps<OptionTypeRenderer['CustomizableDropDownOption']> & {
    form: UseFormReturn<any>
    selectedVariantSku?: string | null
    product: Pick<CustomizableProductInterface, 'options'>
    colorSpecOptionValue?: CustomizableDropDownValue
    fouroverOptionModifier?: string
  }
>((props) => {
  const {
    uid,
    required,
    optionIndex,
    index,
    title,
    dropdownValue,
    form,
    product,
    colorSpecOptionValue,
    fouroverOptionModifier,
  } = props
  const { watch, getValues, register, setValue, getFieldState } = form

  const enteredOptions = watch('enteredOptions')

  const handleOptionSelect = (newValue: string) => {
    const entered = Array.isArray(enteredOptions) ? [...enteredOptions].filter((item) => item) : []
    if (entered.find((o) => o.uid === uid)) {
      entered.find((o) => o.uid === uid).value = newValue
    } else {
      entered.push({ uid, value: newValue })
    }
    setValue(`enteredOptions`, entered, {})
  }
  const value = enteredOptions ? enteredOptions.find((o) => o?.uid === uid)?.value : ''
  useEffect(() => {
    if (uid) {
      const firstOptionId = dropdownValue?.at(0)?.uid?.toString()
      form.register(`enteredOptions.${index}`, {
        required: required || false,
        value: {
          uid,
          value: firstOptionId,
        },
        validate: (v) => {
          if (required && !v?.value) {
            return false
          }
          return true
        },
      })
      form.setValue(`enteredOptions.${index}`, {
        uid,
        value: firstOptionId,
      })
    }
  }, [form.register, form.setValue, required, index, uid, title, dropdownValue])

  if (
    !value ||
    !colorSpecOptionValue ||
    !dropdownValue ||
    (fouroverOptionModifier === 'DisableIfSingle' && dropdownValue?.length < 2) ||
    fouroverOptionModifier === 'Disable'
  ) {
    return null
  }

  return (
    <div key={uid} className='my-[15px]'>
      <StandardDropdown
        value={value}
        label={title || ''}
        onChange={(newValue) => {
          if (uid && newValue?.target?.value) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            handleOptionSelect(newValue?.target?.value?.toString())
          }
        }}
        options={filterNonNullableKeys(dropdownValue, ['title']).map((option) => (
          <MenuItem value={option.uid} className='flex w-full items-center justify-between'>
            <span>{option.title}</span>
            {!!option.price && option.price > 0 && (
              <>
                +<Money value={option.price} />
              </>
            )}
          </MenuItem>
        ))}
        twoColumns
        fullWidth
        placeholder='Select...'
      />
    </div>
  )
})

const CustomizableFieldOptionRenderer = React.memo<
  React.ComponentProps<OptionTypeRenderer['CustomizableFieldOption']> & {
    form: UseFormReturn<any>
    selectedVariantSku?: string | null
  }
>((props) => {
  const { uid, title, form, selectedVariantSku, index, required } = props
  const { simples, color_list } = props as CustomizableFieldOption
  const { setValue, watch, getFieldState } = form

  const shouldBeRequired = selectedVariantSku && simples?.includes(selectedVariantSku) && required

  useEffect(() => {
    if (uid) {
      form.register(`enteredOptions.${index}`, {
        required: shouldBeRequired || false,
        value: {
          uid,
          value: color_list?.find((color) => color && color.default)
            ? color_list?.find((color) => color && color.default)?.code
            : '',
        },
        validate: ({ value }) => {
          if (shouldBeRequired && !value) {
            return false
          }
          return true
        },
      })
    }
  }, [form, color_list, shouldBeRequired, index, uid, title])

  useEffect(
    () => () => {
      form.unregister(`enteredOptions.${index}`)
    },
    [title, form.unregister, index],
  )

  const colorOptionRenderer = useCallback((option, label, optionIndex, inMainArea) => {
    if (optionIndex === 1) {
      return (
        <div
          className='inline-block h-[22px] w-[75%] min-w-[50px] rounded-[3px] border-[0.5px] border-20-grey'
          style={{ backgroundColor: label.toString() }}
        />
      )
    }
    if (inMainArea && optionIndex === 0) {
      return null
    }
    return <>{label}</>
  }, [])

  const enteredOptions = watch('enteredOptions')

  const handleColorSelect = useCallback(
    (newValue: string) => {
      const entered = Array.isArray(enteredOptions)
        ? [...enteredOptions].filter((item) => item)
        : []
      if (entered.find((o) => o.uid === uid)) {
        entered.find((o) => o.uid === uid).value = newValue
      } else {
        entered.push({ uid, value: newValue })
      }
      setValue(`enteredOptions`, entered, {})
    },
    [setValue, uid, enteredOptions],
  )

  return (
    <div>
      <div key={uid} className='my-[15px]'>
        <Dropdown
          value={enteredOptions ? enteredOptions.find((o) => o.uid === uid)?.value : ''}
          placeholder='Select...'
          options={
            color_list?.map((color) => ({
              labels: [color?.label || '', color?.code || ''],
              value: color?.code || '',
            })) || []
          }
          onChange={(newValue) => {
            if (uid && newValue.value) {
              handleColorSelect(newValue.value as string)
            }
          }}
          label={title || ''}
          optionLabelCountInMainArea={1}
          optionLabelRenderer={colorOptionRenderer}
          mainBorderClassName={`border-[1px] ${
            getFieldState(`enteredOptions.${index}`).invalid
              ? 'border-[#DF1615]'
              : 'border-tight-black'
          }`}
        />
      </div>
    </div>
  )
})

const defaultRenderer = {
  CustomizableAreaOption,
  CustomizableCheckboxOption: () => <div>checkbox not implemented</div>,
  CustomizableDateOption: () => <div>date not implemented</div>,
  // eslint-disable-next-line react/destructuring-assignment
  CustomizableDropDownOption: (props) => {
    const { title } = props
    if (title === 'Colorspec') {
      return (
        <div className='my-[15px]'>
          <ColorspecDropDownOption {...props} />
        </div>
      )
    }
    if (title === 'Turn Around Time') {
      return <TurnAroundTimeDropDownOption {...props} />
    }
    return <CustomizableDropDownOption {...props} />
  },
  CustomizableFieldOption: CustomizableFieldOptionRenderer,
  CustomizableFileOption: () => <div>file not implemented</div>,
  CustomizableMultipleOption: () => <div>multi not implemented</div>,
  CustomizableRadioOption: () => <div>radios not implemented</div>,
}

type Simplify<T> = { [KeyType in keyof T]: T[KeyType] }

type MissingOptionTypeRenderer = Omit<OptionTypeRenderer, keyof typeof defaultRenderer>
type DefinedOptionTypeRenderer = Partial<Pick<OptionTypeRenderer, keyof typeof defaultRenderer>>

type OptionTypeRendererProp = Simplify<
  keyof MissingOptionTypeRenderer extends never
    ? (MissingOptionTypeRenderer & DefinedOptionTypeRenderer) | undefined
    : MissingOptionTypeRenderer & DefinedOptionTypeRenderer
>

type ProductCustomizableProps = {
  product: Pick<ConfigurableProduct, 'options'> & ProductCustomizableFragment
  index?: number
  form: UseFormReturn<any>
  selectedVariantSku?: string | null
  quantities: Record<number, number>
  colorSpecOptionValue?: CustomizableDropDownValue | null
  filter?: (option: CustomizableOptionInterface) => boolean
  fouroverOptionModifiers?: FourOverCustomOptionModifier[]
} & (keyof MissingOptionTypeRenderer extends never
  ? { renderer?: OptionTypeRendererProp }
  : { renderer: OptionTypeRendererProp })

export function ProductCustomizable(props: ProductCustomizableProps) {
  const {
    product,
    renderer,
    index = 0,
    form,
    selectedVariantSku,
    quantities,
    colorSpecOptionValue,
    fouroverOptionModifiers,
    filter = () => true,
  } = props

  const filterOptionsBySelectedVariant = useCallback(
    (option) => {
      if ('color_list' in option) {
        const { simples } = option as CustomizableFieldOption
        if (Array.isArray(simples) && selectedVariantSku && !simples.includes(selectedVariantSku)) {
          return false
        }
      }
      return true
    },
    [selectedVariantSku],
  )

  return (
    <>
      {filterNonNullableKeys(product.options, ['sort_order'])
        .filter(filterOptionsBySelectedVariant)
        .filter(filter)
        .sort((o1, o2) => (o1?.sort_order > o2?.sort_order ? 1 : -1))
        .map((option, oIndex) => (
          <RenderType
            key={option.uid}
            renderer={{ ...defaultRenderer, ...renderer }}
            {...option}
            form={form}
            selectedVariantSku={selectedVariantSku}
            optionIndex={option.sort_order + 100}
            index={oIndex}
            product={product}
            quantities={quantities}
            colorSpecOptionValue={colorSpecOptionValue}
            fouroverOptionModifier={
              fouroverOptionModifiers?.find((m) => m?.optionLabel === option.title)?.modifier
            }
          />
        ))}
    </>
  )
}
